<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { useQueryClient } from '@tanstack/vue-query'
import type { Elemental, ElementalFormData } from '~~/src/types/elemental'
import { Button } from '@/components/ui/button'
import Switch from '@/components/ui/switch/Switch.vue'
import { useCreateElemental } from '~~/src/composables/mutations/elemetal/useCreateElemental'
import { insetListOnElementalsLists } from '~~/src/composables/mutations/elemetal/useSaveElementalInAList'
const props = defineProps<Props>()
const emits = defineEmits<{
  (event: 'onCreate', id: string): void
}>()
defineOptions({
  name: 'CreateListForm',
})

interface Props {
  element?: Elemental | null
  type?: ElementalTypeEnum | null

}

const { handleSubmit } = useForm<{ title: string; isPrivate: boolean }>()
const queryClient = useQueryClient()

const { mutate, isPending } = useCreateElemental()
const toast = useToast()

const createList = handleSubmit(({ title, isPrivate }: { title: string; isPrivate: boolean }) => {
  if (!props.element)
    return

  const id = props.element.id
  mutate({
    elemental: {
      title,
      isPrivate,
      itemIds: [id],
      elementalTypeId: ElementalTypeEnum.List,
      contentTypeId: props.element?.contentTypeId,
    } as ElementalFormData,
    elementalType: ElementalTypeEnum.List,
  },
  {
    onSuccess(result) {
      const listId = result?.id
      if (listId) {
        queryClient.setQueriesData<Elemental | null>({
          queryKey: ['elemental', props.type, id],
          exact: false,
        }, insetListOnElementalsLists(listId))
        if (props.element?.url) {
          queryClient.setQueriesData<Elemental | null>({
            queryKey: ['elemental', props.type, props.element?.url],
            exact: false,
          }, insetListOnElementalsLists(listId))
        }
        emits('onCreate', listId)
      }
    },
    onError(error) {
      return toast.warning(error.message, { id: 'save-error' })
    },
  })
})

const { value: title } = useField<string>('title')
const { value: isPrivate } = useField<boolean>('isPrivate')
</script>

<template>
  <form class="flex flex-col gap-6  bg-white" @submit="createList">
    <label class="flex items-center px-2 p-1 gap-2 bg-grayLighter border border-softGray rounded-2 focus-within:outline-darkerGray ">
      <input v-model="title" placeholder="Enter list name" class="min-w-0 w-full bg-transparent focus:outline-none" tabindex="1" :disabled="isPending">
    </label>
    <div class="flex items-center gap-4">
      <label for="isprivate" class="flex items-center gap-2 min-w-fit">
        <Switch id="isprivate" v-model:checked="isPrivate" :disabled="isPending" />
        <span class="text-softBlack font-inter font-700 text-xs">
          Private List
        </span>
      </label>
      <Button variant="snack" size="sm" type="submit" class="w-full max-w-sm ml-a" :disabled="isPending">
        Create
      </Button>
    </div>
  </form>
</template>
